// Chat Messaging Module - 메시지 전송 및 처리 로직
// 메인 chat_controller.js에서 import하여 사용

import { getI18nMessage } from '../../lib/i18n_helper'
import { safeFocus } from '../../lib/ios_focus_guard'
import { showError as showErrorToast } from '../../lib/platform_toast'
import { NativeBridge } from '../../lib/native_bridge'
import { createLogger } from '../../lib/logger'

const log = createLogger('ChatMessaging')

export const MessagingMixin = {
  // 메시지 전송
  sendMessage(event) {
    if (this.httpFallbackSubmitting) {
      this.httpFallbackSubmitting = false
      return
    }

    event.preventDefault()

    const content = this.inputTarget.value.trim()
    if (!content) return

    // A retry of the same draft keeps its client id, so the server stores it once even if the first try landed
    if (content !== this.lastDraft || !this.pendingClientId) this.pendingClientId = this.newClientId()
    this.lastDraft = content
    this.sendAttemptTime = Date.now()

    // ✅ 연결 상태 확인
    if (!this.subscription || !this.isConnected) {
      console.warn('[Chat] WebSocket not ready, falling back to HTTP')
      this.fallbackToHTTP()
      return
    }

    // ✅ 전송 후 타임아웃 설정 (BusinessConstants::UITiming::MESSAGE_SEND_TIMEOUT_MS)
    // ⭐ Optional chaining with fallback to prevent errors if BusinessConstants not loaded
    const timeoutMs = window.BusinessConstants?.UITiming?.MESSAGE_SEND_TIMEOUT_MS || 5000
    this.sendTimeout = setTimeout(() => {
      const timeoutMsg = getI18nMessage(this.element, 'errorsSendTimeout', '訊息傳送逾時')
      this.handleSendFailure(timeoutMsg)
    }, timeoutMs)

    this.subscription.perform("send_message", { content: content, client_id: this.pendingClientId })
    NativeBridge.Haptic.impact("light")
    // ⚠️ 입력 비우지 않음! 서버 응답 대기
  },

  async handleTurboSubmitEnd(event) {
    const { success, fetchResponse } = event.detail
    if (success || !fetchResponse || fetchResponse.statusCode !== 403) return
    if (!fetchResponse.contentType?.includes("application/json")) return

    try {
      const data = JSON.parse(await fetchResponse.responseText)
      if (data.needs_verification) {
        this.handleVerificationRequired(data.error)
      }
    } catch (error) {
      log.warn('Failed to parse Turbo submit error response', error)
    }
  },

  // ✅ 전송 성공 핸들러 (echo of our message, or an ack for a retried draft)
  // A late echo clears the input only while it still holds that text, so nothing typed since is lost.
  handleSendSuccess(sentContent = this.lastDraft) {
    clearTimeout(this.sendTimeout)

    const current = this.inputTarget.value.trim()
    if (!current || current === sentContent) {
      this.inputTarget.value = ""
      this.inputTarget.style.height = "auto"
    }
    if (sentContent === this.lastDraft) {
      this.lastDraft = null
      this.pendingClientId = null
    }

    // 입력창 포커스 유지 (UX 개선, safeFocus handles iOS keyboard timing)
    safeFocus(this.inputTarget)
  },

  // ✅ 전송 실패 핸들러
  handleSendFailure(errorMessage) {
    clearTimeout(this.sendTimeout)

    // 입력 복원
    if (this.lastDraft) {
      this.inputTarget.value = this.lastDraft
    }

    // 에러 토스트 표시
    const defaultMsg = getI18nMessage(this.element, 'errorsSendFailed', '訊息傳送失敗。請再試一次。')
    this.showErrorToast(errorMessage || defaultMsg)
  },

  handleVerificationRequired(errorMessage) {
    clearTimeout(this.sendTimeout)

    if (this.lastDraft) {
      this.inputTarget.value = this.lastDraft
    }

    this.isConnected = false

    if (this.subscription) {
      this.subscription.unsubscribe()
      this.subscription = null
    }

    if (this.hasInputTarget) {
      this.inputTarget.disabled = true
    }

    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = true
    }

    const gpsController = this.gpsController()
    if (gpsController) {
      gpsController.show()
      gpsController.showError(errorMessage || this.locationVerificationRequiredMessage())
      return
    }

    this.showErrorToast(errorMessage || this.locationVerificationRequiredMessage())
  },

  gpsController() {
    const gpsElement = document.querySelector('[data-controller~="gps"][data-gps-target="overlay"]')
    if (!gpsElement) return null

    return this.application.getControllerForElementAndIdentifier(gpsElement, "gps")
  },

  handleGpsVerified() {
    Turbo.visit(window.location.href, { action: "replace" })
  },

  // ✅ 재시도 버튼 핸들러
  retryLastMessage() {
    if (this.lastDraft) {
      this.inputTarget.value = this.lastDraft
      this.sendMessage(new Event('submit'))
    }
  },

  // ✅ HTTP Fallback
  fallbackToHTTP() {
    const form = this.formTarget

    // Same client id as the WebSocket attempt: the server stores the draft once whichever path lands
    form.querySelector('input[name="idempotency_key"]')?.remove()
    const input = document.createElement('input')
    input.type = 'hidden'
    input.name = 'idempotency_key'
    input.value = this.pendingClientId || this.newClientId()
    form.appendChild(input)

    this.httpFallbackSubmitting = true

    // 폼 제출 (Turbo 자동 처리)
    form.requestSubmit()
  },

  newClientId() {
    return window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`
  },

  // ✅ 에러 토스트 표시 (Platform-aware: iOS native / Web)
  showErrorToast(message) {
    // Platform-aware toast: iOS uses native toast, Web/Android uses web toast
    // This ensures toasts are visible above native carousel on iOS
    // Plain text: the web toast escapes it server-side and the iOS native toast is a label.
    showErrorToast(message)
  }
}
